import { userSchema, usersWithAssignedRolesSchema } from "@/schemas/user.schema";
import { api as baseApi, apiJS as baseApiJS } from "@/store/api";
import { User, UserWithAssignedRoles } from "@/types/user.types";

export const TAG_USER = "USER";
export const TAG_IMPERSONATION_USERS = "USER";
export type UserImpersonation = { userId: string; name: string };

interface UserSettingsParams {
    userId: string;
    params: { name: string; value: any };
}

const apiJS = baseApiJS
    .enhanceEndpoints({
        addTagTypes: [TAG_USER],
    })
    .injectEndpoints({
        endpoints: (builder) => ({
            getUser: builder.query<User, void>({
                query: () => ({ url: "me", method: "GET" }),
                transformResponse: (response) => {
                    const result = userSchema.safeParse(response);
                    if (result.success) {
                        return result.data as User;
                    } else {
                        console.error("Received user data in a wrong format");
                        throw (result as any).error;
                    }
                },
                providesTags: [TAG_USER],
            }),
            getAllUsersWithAssignedRoles: builder.query<UserWithAssignedRoles[], void>({
                query: () => ({ url: "admin/users", method: "GET" }),
                transformResponse: (response) => {
                    const result = usersWithAssignedRolesSchema.safeParse(response);
                    if (result.success) {
                        return result.data as UserWithAssignedRoles[];
                    } else {
                        console.error("Received users with assigned roles data in a wrong format");
                        throw (result as any).error;
                    }
                },
            }),
        }),
    });

const api = baseApi
    .enhanceEndpoints({
        addTagTypes: [TAG_IMPERSONATION_USERS],
    })
    .injectEndpoints({
        endpoints: (builder) => ({
            getImpersonationUsers: builder.query<UserImpersonation[], void>({
                query: () => ({ url: "impersonation/user", method: "GET" }),
                providesTags: [{ type: TAG_IMPERSONATION_USERS }],
            }),
            putUserSettings: builder.mutation<void, UserSettingsParams>({
                query: ({ userId, params }) => ({
                    url: `user/${userId}/setting`,
                    method: "PUT",
                    body: params,
                }),
                async onQueryStarted(_, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(apiJS.util.invalidateTags([TAG_USER]));
                },
            }),
        }),
    });

export { apiJS as userApi };
export const { useGetUserQuery, useGetAllUsersWithAssignedRolesQuery } = apiJS;
export const { useGetImpersonationUsersQuery, usePutUserSettingsMutation } = api;
